/**
 * Purpose: Client wrapper for OPFS Web Worker operations
 *
 * How it works:
 * 1. Lazily initializes a dedicated worker for OPFS operations
 * 2. Routes large file operations through the worker
 * 3. Falls back to main thread for small files or if worker fails
 *
 * Key features:
 * - Promise-based API for worker communication
 * - Automatic fallback to main thread operations
 * - Request ID tracking for concurrent operations
 */

import type { WorkerMessage, WorkerResponse } from "./opfsWorker";

/** Threshold for using worker (files >= 10MB use worker) */
const WORKER_THRESHOLD = 10 * 1024 * 1024;

let worker: Worker | null = null;
let isWorkerSupported = true;
const pendingRequests = new Map<
  string,
  { resolve: (value: WorkerResponse) => void; reject: (error: Error) => void }
>();

/**
 * Generate unique request ID
 */
function generateRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Initialize the OPFS worker
 */
function initWorker(): Worker | null {
  if (worker) {
    return worker;
  }
  if (!isWorkerSupported) {
    return null;
  }

  try {
    // Create worker using URL constructor for module workers
    worker = new Worker(new URL("./opfsWorker.ts", import.meta.url), {
      type: "module",
    });

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const { id } = event.data;
      const pending = pendingRequests.get(id);
      if (pending) {
        pending.resolve(event.data);
        pendingRequests.delete(id);
      }
    };

    worker.onerror = (error) => {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.error("[OPFS Worker] Error:", error);
      }
      // Mark worker as unsupported and reject all pending requests
      isWorkerSupported = false;
      worker?.terminate();
      worker = null;

      pendingRequests.forEach((pending) => {
        pending.reject(new Error("Worker failed"));
      });
      pendingRequests.clear();
    };

    return worker;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[OPFS Worker] Failed to initialize:", error);
    }
    isWorkerSupported = false;
    return null;
  }
}

/**
 * Send a message to the worker and wait for response
 */
function sendToWorker(
  message: Omit<WorkerMessage, "id">,
): Promise<WorkerResponse> {
  return new Promise((resolve, reject) => {
    const w = initWorker();
    if (!w) {
      reject(new Error("Worker not available"));
      return;
    }

    const id = generateRequestId();

    // The write payload rides as a Blob (structured-cloned by reference — no
    // byte copy), and the worker streams it into the sync access handle. So the
    // main thread never materialises the whole file, which the old
    // blob.arrayBuffer() did before this even reached postMessage.
    //
    // Register the pending entry AFTER a successful post, not before: a throwing
    // postMessage then rejects the promise (executor throw) without leaving an
    // orphaned entry, and the worker's reply can't be delivered before this
    // synchronous frame ends, so there is no window to miss it.
    w.postMessage({ ...message, id } as WorkerMessage);
    pendingRequests.set(id, { resolve, reject });

    // Timeout after 30 seconds
    setTimeout(() => {
      if (pendingRequests.has(id)) {
        pendingRequests.delete(id);
        reject(new Error("Worker timeout"));
      }
    }, 30000);
  });
}

/**
 * Check if a blob should use the worker for processing
 */
export function shouldUseWorker(blobSize: number): boolean {
  return isWorkerSupported && blobSize >= WORKER_THRESHOLD;
}

/**
 * Write a blob to OPFS using worker (for large files)
 *
 * @param attachmentId - Unique identifier
 * @param blob - The blob to write
 * @returns true if successful
 */
export async function writeWithWorker(
  attachmentId: string,
  blob: Blob,
): Promise<boolean> {
  try {
    // Hand the Blob straight to the worker — no main-thread arrayBuffer(). A
    // structured-cloned Blob shares its immutable backing store rather than
    // copying bytes, so peak main-thread cost is a handle, not the file size.
    const response = await sendToWorker({
      type: "write",
      attachmentId,
      data: blob,
    });
    return response.success;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[OPFS Worker] Write failed, falling back:", error);
    }
    return false;
  }
}

/**
 * Read from OPFS using worker
 *
 * @param attachmentId - Unique identifier
 * @returns Blob if found, null otherwise
 */
export async function readWithWorker(
  attachmentId: string,
): Promise<Blob | null> {
  try {
    const response = await sendToWorker({
      type: "read",
      attachmentId,
    });

    if (response.success && response.data) {
      return new Blob([response.data]);
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Delete from OPFS using worker
 *
 * @param attachmentId - Unique identifier
 * @returns true if deleted
 */
export async function deleteWithWorker(attachmentId: string): Promise<boolean> {
  try {
    const response = await sendToWorker({
      type: "delete",
      attachmentId,
    });
    return response.success;
  } catch {
    return false;
  }
}

/**
 * Check if worker is available and supported
 */
export function isWorkerAvailable(): boolean {
  return isWorkerSupported && typeof Worker !== "undefined";
}

/**
 * Terminate the worker (for cleanup)
 */
export function terminateWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
  pendingRequests.clear();
}
